/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Swaps the variant inventory-notice message on `variant:update`.
 * Optionally hides the product-level notice when the selected variant
 * has its own message (`product_notice_mode=hide_when_variant`).
 * Show/hide uses CSS opacity transitions (see block stylesheet).
 */
class VariantInventoryNotice extends Component {
  /** @type {Record<string, string>} */
  #variantNotices = {};
  /** @type {'hide_when_variant' | 'alongside' | 'off'} */
  #productNoticeMode = 'hide_when_variant';
  /** @type {boolean} */
  #hasProductNotice = false;
  /** @type {HTMLElement | null} */
  #productNoticeEl = null;
  /** @type {HTMLElement | null} */
  #variantNoticeEl = null;
  /** @type {HTMLElement | null} */
  #variantNoticeTextEl = null;
  /** @type {number | null} */
  #swapTimeoutId = null;

  connectedCallback() {
    super.connectedCallback();

    const jsonEl = this.querySelector('[data-variant-notices]');
    try {
      this.#variantNotices = JSON.parse(jsonEl?.textContent || '{}');
    } catch {
      this.#variantNotices = {};
    }

    this.#productNoticeMode = /** @type {'hide_when_variant' | 'alongside' | 'off'} */ (
      this.dataset.productNoticeMode || 'hide_when_variant'
    );
    this.#hasProductNotice = this.dataset.hasProductNotice === 'true';
    this.#productNoticeEl = this.querySelector('[data-product-notice]');
    this.#variantNoticeEl = this.querySelector('[data-variant-notice]');
    this.#variantNoticeTextEl = this.querySelector('[data-variant-notice-text]');

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, quick-view-drawer');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#swapTimeoutId !== null) {
      window.clearTimeout(this.#swapTimeoutId);
      this.#swapTimeoutId = null;
    }
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, quick-view-drawer');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant?.id) return;

    const message = this.#variantNotices[String(variant.id)] || '';
    const hasVariantNotice = message.length > 0;

    let showProductNotice = false;
    if (this.#hasProductNotice) {
      if (this.#productNoticeMode === 'alongside') {
        showProductNotice = true;
      } else if (this.#productNoticeMode === 'hide_when_variant') {
        showProductNotice = !hasVariantNotice;
      }
    }

    this.#setVisible(this.#productNoticeEl, showProductNotice);
    this.#setVariantNotice(message, hasVariantNotice);
    this.#setVisible(this, hasVariantNotice || showProductNotice);
  };

  /**
   * @param {HTMLElement | null} el
   * @param {boolean} visible
   */
  #setVisible(el, visible) {
    if (!el) return;
    el.hidden = !visible;
  }

  /**
   * Updates variant notice text. When already visible and the message
   * changes, fades out → swap → fades in for a smoother handoff.
   * @param {string} message
   * @param {boolean} visible
   */
  #setVariantNotice(message, visible) {
    const el = this.#variantNoticeEl;
    // Text lives in an inner span so swapping the message never clobbers
    // sibling content appended after it (e.g. the TECH-AIR returns link).
    const textEl = this.#variantNoticeTextEl;
    if (!el || !textEl) return;

    if (this.#swapTimeoutId !== null) {
      window.clearTimeout(this.#swapTimeoutId);
      this.#swapTimeoutId = null;
      el.classList.remove('is-swapping');
    }

    const wasVisible = !el.hidden;
    const previousMessage = textEl.textContent || '';

    if (!visible) {
      el.hidden = true;
      return;
    }

    if (!wasVisible) {
      textEl.textContent = message;
      el.hidden = false;
      return;
    }

    if (previousMessage === message) return;

    el.classList.add('is-swapping');
    const durationMs = this.#getTransitionMs(el);

    this.#swapTimeoutId = window.setTimeout(() => {
      textEl.textContent = message;
      el.classList.remove('is-swapping');
      this.#swapTimeoutId = null;
    }, durationMs);
  }

  /** @param {HTMLElement} el */
  #getTransitionMs(el) {
    const raw = getComputedStyle(el).getPropertyValue('--vin-transition-ms').trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 200;
  }
}

if (!customElements.get('variant-inventory-notice')) {
  customElements.define('variant-inventory-notice', VariantInventoryNotice);
}
